import { useState } from 'react';
import { motion } from 'framer-motion';
import { FileText, Download, Lock, Mail, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import SEO from '../components/SEO';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Notes = () => {
  const [email, setEmail] = useState('');
  const [isVerified, setIsVerified] = useState(false);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [verifiedEmail, setVerifiedEmail] = useState('');

  const handleVerifyEmail = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/notes/verify`, { email });
      setNotes(response.data.notes);
      setVerifiedEmail(response.data.email);
      setIsVerified(true);
      
      if (response.data.notes.length === 0) {
        toast.info('No notes available for your email.');
      } else {
        toast.success(`Access granted! Found ${response.data.notes.length} note(s).`);
      }
    } catch (error) {
      toast.error('Unable to verify email. Please check and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (noteId, filename) => {
    try {
      const response = await axios.get(
        `${API}/notes/download/${noteId}?email=${verifiedEmail}`,
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Download started!');
    } catch (error) {
      toast.error('Error downloading file. Please try again.');
    }
  };

  return (
    <div className="pt-16 md:pt-20">
      <SEO
        title="Study Notes for Enrolled Students — Genius Academy"
        description="Secure PDF study notes and course materials for enrolled Genius Academy students. Verify your email to download notes for MS Office, Tally, Python, Web Development and more."
        path="/notes"
        keywords="Genius Academy notes, computer course notes, MS Office notes, Tally notes, Python notes Kalaburagi"
        noindex
        breadcrumbs={[{ name: 'Home', path: '/' }, { name: 'Study Notes', path: '/notes' }]}
      />
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-slate-50 to-cyan-50 py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center"
          >
            <div className="bg-cyan-100 w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <FileText className="w-10 h-10 text-cyan-600" />
            </div>
            <h1 className="text-4xl md:text-6xl font-bold tracking-tight text-slate-900 mb-6" data-testid="notes-page-title">
              Study <span className="text-cyan-600">Notes</span>
            </h1>
            <p className="text-base md:text-lg text-slate-600 max-w-2xl mx-auto leading-relaxed">
              Access course materials and study notes. Enter your registered email to view available resources.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Email Verification / Notes Display */}
      <section className="py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {!isVerified ? (
            /* Email Verification Form */
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-white rounded-2xl border border-slate-100 shadow-lg p-8"
            >
              <div className="flex items-center gap-3 mb-6">
                <Lock className="w-6 h-6 text-cyan-600" />
                <h2 className="text-2xl font-bold text-slate-900">Verify Your Email</h2>
              </div>
              
              <form onSubmit={handleVerifyEmail} className="space-y-6">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-2">
                    Registered Email Address
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <input
                      type="email"
                      id="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="bg-white border border-slate-200 rounded-lg pl-12 pr-4 py-3 focus:ring-2 focus:ring-cyan-500 focus:border-transparent outline-none transition-all w-full"
                      placeholder="your.email@example.com"
                      data-testid="notes-email-input"
                    />
                  </div>
                  <p className="text-sm text-slate-500 mt-2">
                    Only registered students can access course notes.
                  </p>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-cyan-600 hover:bg-cyan-700 disabled:bg-slate-300 text-white rounded-full px-8 py-4 font-bold shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-1 disabled:transform-none"
                  data-testid="verify-email-button"
                >
                  {loading ? 'Verifying...' : 'Access Notes'}
                </button>
              </form>
            </motion.div>
          ) : (
            /* Notes List */
            <div>
              <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-8 flex items-center gap-3">
                <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0" />
                <div>
                  <p className="font-bold text-green-900">Access Granted</p>
                  <p className="text-sm text-green-700">Logged in as: {verifiedEmail}</p>
                </div>
              </div>

              {notes.length === 0 ? (
                <div className="bg-white rounded-2xl border border-slate-100 p-12 text-center">
                  <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-slate-900 mb-2">No Notes Available</h3>
                  <p className="text-slate-600">There are no study notes available for your email yet.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {notes.map((note) => (
                    <motion.div
                      key={note.note_id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="bg-white rounded-2xl border border-slate-100 shadow-sm hover:shadow-md transition-all p-6"
                      data-testid={`note-card-${note.note_id}`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex gap-4 flex-1">
                          <div className="bg-cyan-100 w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0">
                            <FileText className="w-6 h-6 text-cyan-600" />
                          </div>
                          <div className="flex-1">
                            <h3 className="text-xl font-bold text-slate-900 mb-2">{note.title}</h3>
                            {note.description && (
                              <p className="text-slate-600 mb-3">{note.description}</p>
                            )}
                            <div className="flex items-center gap-4 text-sm text-slate-500">
                              <span>📄 {note.filename}</span>
                              <span>📅 {new Date(note.created_at).toLocaleDateString()}</span>
                            </div>
                          </div>
                        </div>
                        <button
                          onClick={() => handleDownload(note.note_id, note.filename)}
                          className="bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg px-6 py-3 font-bold flex items-center gap-2 transition-all"
                          data-testid={`download-note-${note.note_id}`}
                        >
                          <Download size={20} />
                          Download
                        </button>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}

              <div className="mt-8 text-center">
                <button
                  onClick={() => {
                    setIsVerified(false);
                    setEmail('');
                    setNotes([]);
                  }}
                  className="text-slate-600 hover:text-slate-900 font-medium"
                >
                  ← Switch Account
                </button>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Notes;